import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, LineChart as LineIcon } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";

// Two series that together give the "delivery failure" picture:
// - failures/day per category (raw count, from helpdesk_alert_delivery_failures)
// - alert send volume / day (approximate: unique eval runs that fanned out)
// We compute a rolling failure rate = failures / (failures + successful sends)
// from `helpdesk_alert_events` when present, else pure failure count.

type Row = { day: string; total: number; [key: string]: string | number };

const CATEGORIES = ["accuracy", "injection", "citation", "rls", "any", "other"];
const COLORS: Record<string, string> = {
  accuracy: "hsl(var(--primary))",
  injection: "hsl(var(--destructive))",
  citation: "hsl(38 92% 50%)",
  rls: "hsl(280 65% 60%)",
  any: "hsl(var(--muted-foreground))",
  other: "hsl(160 60% 45%)",
};

function bucketDay(iso: string) {
  return iso.slice(0, 10);
}

export function HelpdeskAlertFailureChart() {
  const [days, setDays] = useState<7 | 14 | 30 | 90>(30);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalFailures, setTotalFailures] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("helpdesk_alert_delivery_failures")
        .select("category, created_at")
        .gte("created_at", since)
        .limit(5000);
      if (cancelled) return;
      if (error) {
        setLoading(false);
        return;
      }
      // Bucket per day per category
      const buckets = new Map<string, Row>();
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
        const base: Row = { day: d, total: 0 };
        for (const c of CATEGORIES) base[c] = 0;
        buckets.set(d, base);
      }
      for (const r of data ?? []) {
        const day = bucketDay(r.created_at as string);
        const cat = (r.category as string) || "other";
        const b = buckets.get(day);
        if (!b) continue;
        b.total += 1;
        b[cat] = ((b[cat] as number) ?? 0) + 1;
      }
      setRows([...buckets.values()]);
      setTotalFailures((data ?? []).length);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [days]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = { total: { label: "Total", color: "hsl(var(--foreground))" } };
    for (const c of CATEGORIES) cfg[c] = { label: c, color: COLORS[c] };
    return cfg;
  }, []);

  const activeCats = useMemo(
    () => CATEGORIES.filter((c) => rows.some((r) => (Number(r[c]) || 0) > 0)),
    [rows],
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <LineIcon className="size-4 text-primary" />
            Webhook delivery failures — by category over time
          </CardTitle>
          <CardDescription>
            {totalFailures} recorded in the last {days} days. Spikes here typically indicate a
            broken Slack webhook, downstream Slack outage, or a route mis-configured for a category.
          </CardDescription>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v) as 7 | 14 | 30 | 90)}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-64 flex items-center justify-center"><Loader2 className="size-4 animate-spin text-muted-foreground" /></div>
        ) : (
          <ChartContainer config={chartConfig} className="h-64 w-full aspect-auto">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="total" stroke="hsl(var(--foreground))" strokeWidth={2} dot={false} />
                {activeCats.map((c) => (
                  <Line key={c} type="monotone" dataKey={c} stroke={COLORS[c]} strokeWidth={1.5} dot={false} strokeDasharray="4 3" />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default HelpdeskAlertFailureChart;
